import { fetchRetry } from '@/utils/api/fetchRetry';

/**
 * @author Alejandro
 * @description Get the team seasons by its id
 * @param {Object} params
 * @param {string} params.teamId
 * @return {Promise}
 */
export async function getSeasonsByTeam({ teamId }) {
  try {
    // version 1:  https://z4xv9k9igf.execute-api.us-west-2.amazonaws.com/prod/seasonsByTeam/${teamId}
    const url = `https://ws8d8ye2ii.execute-api.us-west-2.amazonaws.com/prod/v3/seasonsByTeam/${teamId}`;
    const response = await fetchRetry({ url: url });
    return response.data.seasons;
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @author Alejandro
 * @description Get all teams by a seasonId
 * @param {Object} params
 * @param {string|number} params.seasonId
 * @return {Promise<unknown>}
 */
export async function getTeamsBySeason(params) {
  try {
    const url = `https://d2oco6mqmc.execute-api.us-west-2.amazonaws.com/prod/teamsBySeason/${params.seasonId}`;
    const response = await fetchRetry({ url: url });
    return response.data;
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @author Alejandro
 * @description Get all goals in season, if teamId is provided, gets team goals only.
 * @param {Object} params
 * @param {string|number} params.seasonId
 * @param {(string|number)} params.teamId
 * @return {Promise<unknown>}
 */
export async function getGoalTeamsBySeason({ seasonId, teamId }) {
  try {
    const url = `https://3xbsvx7zre.execute-api.us-west-2.amazonaws.com/prod/goalsTeamBySeason/${seasonId}/${teamId ||
      ''}`;
    return await fetchRetry({ url: url });
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @param {Object} params
 * @param {(number|string)} seasonId
 * @param {(number|string)} teamId
 * @param {boolean} requestDocument
 */
export async function getTeamStatisticsBySeason({ seasonId, teamId }, requestDocument = true) {
  try {
    const options = {
      url: `https://w6ykcz6043.execute-api.us-west-2.amazonaws.com/prod/statsTeamBySeason/${seasonId}/${teamId}`,
    };
    if (requestDocument) {
      options.url = `https://golstats-microservices.s3-us-west-2.amazonaws.com/statsTeamBySeason-${seasonId}-${teamId}.json`;
      options.retries = 1;
    }
    return fetchRetry(options);
  } catch (e) {
    if (requestDocument) {
      return getTeamStatisticsBySeason({ seasonId, teamId }, false);
    }
    throw new Error(e);
  }
}

/**
 * @param {Object} params
 * @param {(number|string)} seasonId
 * @param {(number|string)} teamId
 */
export async function getTeamStatisticsBySeasonMatchAnalysis({ seasonId, teamId }) {
  try {
    // url v1
    //https://a4dcb6avog.execute-api.us-west-2.amazonaws.com/prod/statisticsTeamBySeason/${seasonId}/${teamId}
    return await fetchRetry({
      url: `https://a4dcb6avog.execute-api.us-west-2.amazonaws.com/prod/v2/statisticsTeamBySeason/${seasonId}/${teamId}`,
    });
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @param {Object} params
 * @param {(number|string)} seasonId
 * @param {(number|string)} teamId
 */
export async function getTeamStatisticsBySeasonForMatchAnalysis({ seasonId, teamId }) {
  try {
    return await fetchRetry({
      url: `https://mabnn9axgb.execute-api.us-west-2.amazonaws.com/prod/statisticsTeamBySeasonPremium/${seasonId}/${teamId}`,
    });
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @author Alejandro
 * @description Get all teams by a seasonId version 2
 * @param {Object} params
 * @param {string|number} params.seasonId
 * @return {Promise<AxiosResponse<T>>}
 */
export async function getTeamsBySeasonV2(params) {
  try {
    const url = `https://yjeig444bb.execute-api.us-west-2.amazonaws.com/prod/teamsBySeason/${params.seasonId}`;
    return await fetchRetry({ url: url });
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @author Ricardo
 * @description Get all players by a seasonId
 * @param {Object} params
 * @param {string|number} params.seasonId
 * @return {Promise<AxiosResponse<T>>}
 */
export async function getPlayersBySeason(params) {
  try {
    const url = ` https://ts7thxzaik.execute-api.us-west-2.amazonaws.com/prod/players/seasons/${params.seasonId}`;
    return await fetchRetry({ url: url });
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * @param {Object} params
 * @param {string|number} params.gameId
 * @return {Promise<AxiosResponse<T>>}
 */
export async function getTeamStatisticsByGame(params) {
  try {
    // v1 url
    //https://nckawv8v3b.execute-api.us-west-2.amazonaws.com/prod/statisticsByGame/
    return await fetchRetry({
      url: `https://nckawv8v3b.execute-api.us-west-2.amazonaws.com/prod/v2/statisticsByGame/${params.gameId}`,
    });
  } catch (e) {
    return Promise.reject(e);
  }
}
